import React, { useState, useEffect } from "react";
import {
  Box,
  Grid,
  TextField,
  Autocomplete,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  Button,
  Typography,
  CircularProgress,
  Chip,
  Checkbox,
  IconButton,
  FormControlLabel,
  Paper,
  useTheme
} from "@mui/material";
import { enqueueSnackbar } from "notistack";
import { useLocation } from "react-router-dom";
import servicesList from "../Data/ServicesData";
import ServiceDropdown from "./Servicesdropdown";
import DeductionPreview from "./DeductionPreview";
import { apiUrl } from "./LoginSignup";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import DeleteIcon from "@mui/icons-material/Delete";
import DescriptionIcon from "@mui/icons-material/Description";
import BusinessOutlinedIcon from "@mui/icons-material/BusinessOutlined";
import FactCheckOutlinedIcon from "@mui/icons-material/FactCheckOutlined";
import AccountBalanceWalletOutlinedIcon from "@mui/icons-material/AccountBalanceWalletOutlined";
import GroupAddOutlinedIcon from "@mui/icons-material/GroupAddOutlined";
import NotesOutlinedIcon from "@mui/icons-material/NotesOutlined";
import AttachFileOutlinedIcon from "@mui/icons-material/AttachFileOutlined";
import SendRoundedIcon from "@mui/icons-material/SendRounded";

import Mailer from "./mail";
import { BOOKING_PAYMENT_METHODS } from "../utils/paymentMethods";

const getCurrentDateInput = () => new Date().toISOString().split("T")[0];
const TERM_NUMBERS = Array.from({ length: 10 }, (_, index) => index + 1);
const TERM_LABELS = TERM_NUMBERS.map((termNumber) => `Term ${termNumber}`);

const AddBooking = ({ onClose }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const location = useLocation();
  const [formData, setFormData] = useState({
    branch: "",
    companyName: "",
    contactPerson: "",
    contactNumber: "",
    email: "",
    date: getCurrentDateInput(),
    services: [],
    totalAmount: "",
    selectTerm: "",
    amount: "",
    paymentDate: "",
    closed: "",
    pan: "",
    gst: "",
    notes: "",
    bank: "",
    state: "",
    funddisbursement: "",
    isRefundable: false,
    refundablePercentage: "",
    isApprovalRefundable: false,
    approvalRefundablePercentage: "",
  });
  
  // Document upload state
  const [documents, setDocuments] = useState([]);
  const [documentType, setDocumentType] = useState("others");
  const [paymentProofs, setPaymentProofs] = useState([]);

  const [errors, setErrors] = useState({});
  const [openDialog, setOpenDialog] = useState(false); // Dialog state for popup
  const [bookingId, setBookingId] = useState(null); // Store booking ID
  const [loading, setLoading] = useState(false); // State to manage the loading spinner
  const [companyBranches, setCompanyBranches] = useState([]);
  const normalizePercentInput = (rawValue, finalize = false) => {
    if (rawValue === null || rawValue === undefined) return "";

    let value = String(rawValue).replace(/%/g, "").replace(/\s+/g, "");
    if (!value) return "";

    value = value.replace(/[^0-9.]/g, "");

    const firstDotIndex = value.indexOf(".");
    if (firstDotIndex !== -1) {
      value =
        value.slice(0, firstDotIndex + 1) +
        value.slice(firstDotIndex + 1).replace(/\./g, "");
    }

    if (value.startsWith(".")) {
      value = `0${value}`;
    }

    if (finalize && value.endsWith(".")) {
      value = value.slice(0, -1);
    }

    return value;
  };

  const setNormalizedFormPercent = (name, value, finalize = false) => {
    setFormData((prev) => ({
      ...prev,
      [name]: normalizePercentInput(value, finalize),
    }));
  };

  const setNormalizedSharedPercent = (index, value, finalize = false) => {
    const normalizedValue = normalizePercentInput(value, finalize);
    setSharedPersons((prev) =>
      prev.map((person, personIndex) =>
        personIndex === index ? { ...person, percentage: normalizedValue } : person
      )
    );
  };

  // Handle input changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    if (["refundablePercentage", "approvalRefundablePercentage", "funddisbursement"].includes(name)) {
      setNormalizedFormPercent(name, value);
      return;
    }
    const upperFields = ["companyName", "contactPerson", "pan", "gst"];
    const nextValue = name === "email"
      ? value.toLowerCase()
      : upperFields.includes(name)
        ? value.toUpperCase()
        : value;

    setFormData((prev) => ({
      ...prev,
      [name]: nextValue,
    }));
  };

  const handleSelectTermSourceBooking = (_, booking) => {
    setSelectedSourceBooking(booking || null);
    if (!booking) return;

    setFormData((prev) => ({
      ...prev,
      ...mapBookingToForm(booking, prev.selectTerm),
    }));
  };

  const [deductionCatalog, setDeductionCatalog] = useState(null);
  const [sharedPersons, setSharedPersons] = useState([{ userId: "", percentage: "" }]);
  const [shareCount, setShareCount] = useState(1);
  const [users, setUsers] = useState([]);
  const [termSourceBookings, setTermSourceBookings] = useState([]);
  const [termSearchLoading, setTermSearchLoading] = useState(false);
  const [selectedSourceBooking, setSelectedSourceBooking] = useState(null);

  const projectionLeadId = location?.state?.projectionLeadId || "";
  const projectionPrefill = location?.state?.prefill || null;
  const termPrefillBooking = location?.state?.termPrefillBooking || null;
  const requestedTerm = location?.state?.requestedTerm || "";

  const selectedTermNumber = Number(String(formData.selectTerm || "").replace("Term ", "") || 0);
  const isContinuationTerm = selectedTermNumber >= 2;
  const userSessionForUi = JSON.parse(localStorage.getItem("userSession")) || {};
  const adminRoles = ["admin", "senior admin", "super admin", "director", "dev", "srdev", "sr dev"];
  const isAdminRole = adminRoles.includes((userSessionForUi.user_role || "").toLowerCase());
  const isCashPayment = (paymentMode = "") => String(paymentMode).trim().toLowerCase() === "cash";

  const previewTermKey = `term_${selectedTermNumber || 1}`;
  const previewSource = isContinuationTerm ? selectedSourceBooking : undefined;
  const previewBooking = {
    ...previewSource,
    user_id: previewSource?.user_id || userSessionForUi.user_id,
    bdm: previewSource?.bdm || userSessionForUi.name,
    bank: formData.bank,
    is_refundable: previewSource ? previewSource.is_refundable : formData.isRefundable,
    refundable_percentage: previewSource ? previewSource.refundable_percentage : Number(formData.refundablePercentage || 0),
    services: Array.from(new Set([...(previewSource?.services || []), ...formData.services])),
    [previewTermKey]: Number(formData.amount || 0),
    term_shares: {
      ...(previewSource?.term_shares || {}),
      [previewTermKey]: {
        creator: { user_id: userSessionForUi.user_id, user_name: userSessionForUi.name },
        payment_date: formData.paymentDate,
        payment_mode: formData.bank,
        shared_with: sharedPersons.filter((person) => person.userId && String(person.userId) !== String(userSessionForUi.user_id)).map((person) => ({ user_id: person.userId, percentage: person.percentage === '' ? null : Number(person.percentage) })),
      },
    },
  };

  const formatDateInput = (value) => {
    if (!value) return "";
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return "";
    return d.toISOString().split("T")[0];
  };

  const mapBookingToForm = (booking, forcedTerm = "") => ({
    branch: booking?.branch_name || "",
    companyName: booking?.company_name || "",
    contactPerson: booking?.contact_person || "",
    contactNumber: booking?.contact_no || "",
    email: booking?.email || "",
    date: getCurrentDateInput(),
    services: Array.isArray(booking?.services) ? booking.services : [],
    totalAmount: booking?.total_amount || "",
    selectTerm: forcedTerm || formData.selectTerm,
    amount: "",
    paymentDate: getCurrentDateInput(),
    closed: booking?.closed_by || "",
    pan: booking?.pan || "",
    gst: booking?.gst || "",
    notes: booking?.remark || "",
    bank: booking?.bank || "",
    state: booking?.state || "",
    funddisbursement: booking?.after_disbursement || "",
    isRefundable: Boolean(booking?.is_refundable),
    refundablePercentage: booking?.refundable_percentage || "",
    isApprovalRefundable: Boolean(booking?.is_approval_refundable),
    approvalRefundablePercentage: booking?.approval_refundable_percentage || "",
  });

  useEffect(() => {
    if (!projectionPrefill) return;

    setFormData((prev) => ({
      ...prev,
      branch: projectionPrefill.branch || prev.branch,
      companyName: projectionPrefill.companyName || prev.companyName,
      contactPerson: projectionPrefill.contactPerson || prev.contactPerson,
      contactNumber: projectionPrefill.contactNumber || prev.contactNumber,
      state: projectionPrefill.state || prev.state,
      notes: projectionPrefill.notes || prev.notes,
    }));
  }, [projectionPrefill]);

  useEffect(() => {
    if (!requestedTerm) return;
    setFormData((prev) => ({
      ...prev,
      selectTerm: requestedTerm,
    }));
  }, [requestedTerm]);

  useEffect(() => {
    if (!termPrefillBooking) return;
    setSelectedSourceBooking(termPrefillBooking);
    setFormData((prev) => ({
      ...prev,
      ...mapBookingToForm(termPrefillBooking, requestedTerm || prev.selectTerm),
    }));
  }, [termPrefillBooking, requestedTerm]);

  useEffect(() => {
    if (!isContinuationTerm) {
      setTermSourceBookings([]);
      setSelectedSourceBooking(null);
      return;
    }

    const fetchTermSourceBookings = async () => {
      try {
        setTermSearchLoading(true);
        const userSession = JSON.parse(localStorage.getItem("userSession"));
        if (!userSession) return;

        const isAdmin = ["admin", "dev", "senior admin", "srdev", "super admin"].includes(
          (userSession.user_role || "").toLowerCase()
        );

        const bookingUrl = isAdmin
          ? `${apiUrl}/booking/all`
          : `${apiUrl}/user/bookings/${userSession.user_id}`;

        const response = await fetch(bookingUrl, {
          headers: {
            "Content-Type": "application/json",
            authorization: `${userSession.token}`,
          },
        });

        if (!response.ok) {
          setTermSourceBookings([]);
          return;
        }

        const data = await response.json();
        const bookings = Array.isArray(data?.Allbookings) ? data.Allbookings : Array.isArray(data) ? data : [];

        const eligibleBookings = bookings.filter((booking) => {
          if (!selectedTermNumber || selectedTermNumber <= 1) return false;
          const previousTermKey = `term_${selectedTermNumber - 1}`;
          const currentTermKey = `term_${selectedTermNumber}`;
          return Number(booking[previousTermKey] || 0) > 0 && Number(booking[currentTermKey] || 0) <= 0;
        });

        setTermSourceBookings(eligibleBookings);
      } catch (error) {
        console.error("Failed to fetch continuation term bookings:", error);
        setTermSourceBookings([]);
      } finally {
        setTermSearchLoading(false);
      }
    };

    fetchTermSourceBookings();
  }, [isContinuationTerm, formData.selectTerm, selectedTermNumber]);

  // Fetch all users to populate the Shared With dropdown
  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const userSession = JSON.parse(localStorage.getItem('userSession'));
        if (!userSession) return;

        // Use /user/options so all authenticated roles can access employee options
        const response = await fetch(`${apiUrl}/user/options`, {
          headers: { 'Authorization': userSession?.token || '' }
        });

        if (response.ok) {
          const data = await response.json();
          // Backend returns { users: [...] }
          setUsers(Array.isArray(data.users) ? data.users : []);
        } else {
          // If restricted or error, default to empty to prevent crash
          setUsers([]);
        }
      } catch (error) {
        console.error("Failed to fetch users for sharing:", error);
        setUsers([]); // Fallback to empty array
      }
    };
    fetchUsers();
  }, []);

  // Fetch company branches
  useEffect(() => {
    fetch(`${apiUrl}/company/public`)
      .then(res => res.json())
      .then(data => {
        if (data && data.branches) {
          const branchesArray = data.branches.split(',').map(b => b.trim()).filter(Boolean);
          setCompanyBranches(branchesArray);
        }
      })
      .catch(err => console.error("Error fetching branches:", err));
  }, []);

  // Handle multiple services selection
  const handleServiceChange = (selectedOptions) => {
    setFormData({
      ...formData,
      services: selectedOptions
        ? selectedOptions.map((option) => option.value)
        : [], // Map selected options to an array
    });
  };

  const validate = () => {
    let validationErrors = {};


    // Validation logic (unchanged)
    if (!formData.branch) validationErrors.branch = "Branch is required";
    if (!formData.companyName) validationErrors.companyName = "Company Name is required";
    if (!formData.contactPerson)
      validationErrors.contactPerson = "Contact Person is required";
    const contactNumberRegex = /^\d{10}$/;
    if (
      !formData.contactNumber ||
      !contactNumberRegex.test(formData.contactNumber)
    ) {
      validationErrors.contactNumber =
        "Valid Contact Number is required (10 digits, no spaces)";
    }
    if (!formData.email) validationErrors.email = "Email is required";
    if (!formData.date) validationErrors.date = "Date is required";
    if (!formData.totalAmount || isNaN(formData.totalAmount)) {
      validationErrors.totalAmount = "Valid Total Amount is required";
    }
    if (!formData.selectTerm)
      validationErrors.selectTerm = "Select Term is required";
    if (isContinuationTerm && !selectedSourceBooking) {
      validationErrors.termSource = `Please select existing ${formData.selectTerm === "Term 2" ? "Term 1" : "Term 2"} booking first`;
    }
    if (!formData.amount || isNaN(formData.amount)) {
      validationErrors.amount = "Valid Amount is required";
    }
    if (Number(formData.amount) > Number(formData.totalAmount)) {
      validationErrors.amount =
        "Received Amount cannot be greater than Total Amount";
    }
    if (!formData.paymentDate)
      validationErrors.paymentDate = "Payment Date is required";
    if (!formData.bank) validationErrors.bank = "Payment Mode is required";
    if (!formData.funddisbursement) validationErrors.funddisbursement = "After Fund Disbursement is required";
    if (!isContinuationTerm && formData.isRefundable) {
      const refundablePct = Number(formData.refundablePercentage || 0);
      if (!refundablePct || refundablePct < 0 || refundablePct > 100) {
        validationErrors.refundablePercentage = "Enter deduction percentage between 1 and 100";
      }
    }
    if (!isContinuationTerm && formData.isApprovalRefundable) {
      const approvalRefundablePct = Number(formData.approvalRefundablePercentage || 0);
      if (!approvalRefundablePct || approvalRefundablePct < 0 || approvalRefundablePct > 100) {
        validationErrors.approvalRefundablePercentage = "Enter approval guarantee percentage between 1 and 100";
      }
    }
    if (!isContinuationTerm && formData.isRefundable && formData.isApprovalRefundable) {
      validationErrors.refundableType = "Select only one refundable booking type";
    }
    if (!formData.pan) {
      validationErrors.pan = "PAN Number is required";
    } else {
      const panRegex = /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/;
      if (!panRegex.test(formData.pan.toUpperCase())) {
        validationErrors.pan =
          "Valid PAN is required (e.g. ABCDE1234F)";
      }
    }
    if (!formData.state) validationErrors.state = "State is required";
    if (paymentProofs.length === 0) {
      validationErrors.paymentProof = "Payment proof is required";
    }

    setErrors(validationErrors);
    return Object.keys(validationErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (validate()) {
      setLoading(true); // Show loader when form is being submitted

      const userSession = JSON.parse(localStorage.getItem("userSession"));
      if (!userSession) {
        enqueueSnackbar("User session not found. Please log in again.", {
          variant: "warning",
        });
        setLoading(false);
        return;
      }

      const sharedTotal = sharedPersons.reduce(
        (sum, person) => sum + Number(person.percentage || 0),
        0
      );

      if (sharedTotal > 100) {
        enqueueSnackbar("Total percentage shared cannot exceed 100%", {
          variant: "error",
        });
        setLoading(false);
        return;
      }

      const selectedShares = sharedPersons.filter((person) => person.userId);
      const blankShares = selectedShares.filter((person) => person.percentage === '' || person.percentage == null);
      if (blankShares.length && blankShares.length !== selectedShares.length) {
        enqueueSnackbar('Leave every coworker percentage blank, or enter every percentage.', { variant: 'error' });
        setLoading(false);
        return;
      }
      const receivedAmount = Number(formData.amount);
      const total_amount = Number(formData.totalAmount);
      const buildShareEntries = () =>
        sharedPersons.filter((p) => p.userId && String(p.userId) !== String(userSession.user_id)).map((person) => {
          const userObj = users.find(u => String(u._id) === String(person.userId));
          return {
            user_id: person.userId,
            user_name: userObj ? userObj.name?.toUpperCase() : "COWORKER",
            percentage: person.percentage === '' || person.percentage == null ? null : Number(person.percentage),
          };
        });

      try {
        if (isContinuationTerm && selectedSourceBooking?._id) {
          const termKey = `term_${selectedTermNumber}`;
          const termSharedWith = buildShareEntries();
          const existingSharedWith = Array.isArray(selectedSourceBooking.shared_with)
            ? selectedSourceBooking.shared_with
            : [];
          const accessUsers = new Map(existingSharedWith.map((sw) => [String(sw.user_id), sw]));

          accessUsers.delete(String(userSession.user_id));

          if (String(selectedSourceBooking.user_id) !== String(userSession.user_id)) {
            accessUsers.set(String(userSession.user_id), {
              user_id: userSession.user_id,
              user_name: userSession.name?.toUpperCase() || "UNKNOWN",
              percentage: 0,
            });
          }

          termSharedWith.forEach((sw) => {
            if (String(sw.user_id) !== String(selectedSourceBooking.user_id) && String(sw.user_id) !== String(userSession.user_id)) {
              accessUsers.set(String(sw.user_id), sw);
            }
          });

          const continuationPayload = {
            date: getCurrentDateInput(),
            user_id: selectedSourceBooking.user_id,
            bdm: selectedSourceBooking.bdm || userSession.name?.toUpperCase() || "",
            branch_name: formData.branch,
            company_name: formData.companyName?.toUpperCase() || "",
            contact_person: formData.contactPerson?.toUpperCase() || "",
            email: formData.email?.toLowerCase() || "",
            contact_no: Number(formData.contactNumber),
            payment_date: formData.paymentDate,
            services: Array.from(new Set([
              ...(Array.isArray(selectedSourceBooking.services) ? selectedSourceBooking.services : []),
              ...(Array.isArray(formData.services) ? formData.services : []),
            ].filter(Boolean))),
            total_amount: Number(selectedSourceBooking.total_amount || total_amount || 0),
            pan: formData.pan?.toUpperCase() || "",
            gst: formData.gst?.toUpperCase() || "N/A",
            remark: formData.notes,
            bank: formData.bank,
            state: formData.state,
            status: "Pending",
            after_disbursement: formData.funddisbursement || "",
            funddisbursement: formData.funddisbursement || "",
            is_refundable: Boolean(selectedSourceBooking.is_refundable),
            refundable_percentage: Number(selectedSourceBooking.refundable_percentage || 0),
            is_approval_refundable: Boolean(selectedSourceBooking.is_approval_refundable),
            approval_refundable_percentage: Number(selectedSourceBooking.approval_refundable_percentage || 0),
            shared_with: Array.from(accessUsers.values()),
            term_shares: {
              [termKey]: {
                creator: {
                  user_id: userSession.user_id,
                  user_name: userSession.name?.toUpperCase() || "UNKNOWN",
                },
                payment_date: formData.paymentDate,
                payment_mode: formData.bank,
                shared_with: termSharedWith,
              },
            },
            continuation_of_booking_id: selectedSourceBooking._id,
            continuation_term_key: termKey,
            continuation_term_label: formData.selectTerm,
          };

          continuationPayload[termKey] = receivedAmount;

          const continuationForm = new FormData();
          continuationForm.append("payload", JSON.stringify(continuationPayload));
          paymentProofs.forEach((file) => continuationForm.append("paymentProofs", file));

          const continuationRes = await fetch(`${apiUrl}/booking-approvals`, {
            method: "POST",
            headers: {
              authorization: `${userSession.token}`,
              "user-name": userSession.name || "",
              "user-role": userSession.user_role || "",
            },
            body: continuationForm,
          });

          const continuationData = await continuationRes.json().catch(() => ({}));
          if (!continuationRes.ok) {
            throw new Error(continuationData.message || `Error submitting ${formData.selectTerm} for approval`);
          }

          enqueueSnackbar(`${formData.selectTerm} submitted for approval successfully!`, { variant: "success" });
          setFormData((prev) => ({
            ...prev,
            amount: "",
            paymentDate: getCurrentDateInput(),
          }));
          setPaymentProofs([]);
          setLoading(false);
          if (onClose) onClose();
          return;
        }

        const dataToSubmit = {
          date: getCurrentDateInput(),
          user_id: userSession.user_id,
          bdm: userSession.name?.toUpperCase() || "",
          branch_name: formData.branch,
          company_name: formData.companyName?.toUpperCase() || "",
          contact_person: formData.contactPerson?.toUpperCase() || "",
          email: formData.email?.toLowerCase() || "",
          contact_no: Number(formData.contactNumber),
          services: formData.services,
          total_amount,
          closed_by: formData.closed || "",
          term_1: formData.selectTerm === "Term 1" ? receivedAmount : null,
          term_2: formData.selectTerm === "Term 2" ? receivedAmount : null,
          term_3: formData.selectTerm === "Term 3" ? receivedAmount : null,
          term_4: formData.selectTerm === "Term 4" ? receivedAmount : null,
          term_5: formData.selectTerm === "Term 5" ? receivedAmount : null,
          term_6: formData.selectTerm === "Term 6" ? receivedAmount : null,
          term_7: formData.selectTerm === "Term 7" ? receivedAmount : null,
          term_8: formData.selectTerm === "Term 8" ? receivedAmount : null,
          term_9: formData.selectTerm === "Term 9" ? receivedAmount : null,
          term_10: formData.selectTerm === "Term 10" ? receivedAmount : null,
          payment_date: formData.paymentDate,
          pan: formData.pan?.toUpperCase() || "",
          gst: formData.gst?.toUpperCase() || "N/A",
          remark: formData.notes,
          bank: formData.bank,
          state: formData.state,
          status: "Pending",
          after_disbursement: formData.funddisbursement || "",
          funddisbursement: formData.funddisbursement || "",
          is_refundable: Boolean(formData.isRefundable),
          refundable_percentage: formData.isRefundable ? Number(formData.refundablePercentage || 0) : 0,
          is_approval_refundable: Boolean(formData.isApprovalRefundable),
          approval_refundable_percentage: formData.isApprovalRefundable ? Number(formData.approvalRefundablePercentage || 0) : 0,
          projectionLeadId,
          shared_with: buildShareEntries(),
          term_shares: {
            term_1: {
              creator: {
                user_id: userSession.user_id,
                user_name: userSession.name?.toUpperCase() || "UNKNOWN",
              },
              payment_date: formData.paymentDate,
              payment_mode: formData.bank,
              shared_with: buildShareEntries(),
            },
          },
        };

        if (!isAdminRole) {
          const approvalForm = new FormData();
          approvalForm.append("payload", JSON.stringify(dataToSubmit));
          paymentProofs.forEach((file) => approvalForm.append("paymentProofs", file));

          const approvalResponse = await fetch(`${apiUrl}/booking-approvals`, {
            method: "POST",
            headers: {
              authorization: `${userSession.token}`,
              "user-name": userSession.name || "",
              "user-role": userSession.user_role || "",
            },
            body: approvalForm,
          });

          const approvalData = await approvalResponse.json().catch(() => ({}));
          if (!approvalResponse.ok) {
            throw new Error(approvalData.message || "Error submitting booking for approval");
          }

          enqueueSnackbar("Booking submitted for approval. It will appear in All Bookings after approval.", {
            variant: "success",
          });
          setPaymentProofs([]);
          setLoading(false);
          if (onClose) onClose();
          return;
        }

        const directForm = new FormData();
        directForm.append("payload", JSON.stringify(dataToSubmit));
        paymentProofs.forEach((file) => directForm.append("paymentProofs", file));

        const response = await fetch(`${apiUrl}/booking/addbooking`, {
          method: "POST",
          headers: {
            authorization: `${userSession.token}`,
          },
          body: directForm,
        });

        if (!response.ok) {
          const errData = await response.json().catch(() => ({}));
          throw new Error(errData.message || "Error creating booking");
        }

        const res = await response.json();
        const bookingId = res.booking_id?.toUpperCase?.() || "N/A";
        const bookingIdRaw = res.booking?._id;
        setBookingId(bookingId);
        
        // Upload documents after booking is created
        if (bookingIdRaw && documents.length > 0) {
          await uploadDocuments(bookingIdRaw);
          setDocuments([]);
        }
        
        setOpenDialog(true);

        if (projectionLeadId) {
          try {
            const transferRes = await fetch(`${apiUrl}/projection-leads/${projectionLeadId}/mark-transferred`, {
              method: "PATCH",
              headers: {
                "Content-Type": "application/json",
                authorization: `${userSession?.token || ""}`,
              },
              body: JSON.stringify({ booking_id: bookingId }),
            });

            if (transferRes.ok) {
              enqueueSnackbar("Projection lead transferred to bookings.", {
                variant: "success",
              });
            } else {
              const transferErr = await transferRes.json().catch(() => ({}));
              enqueueSnackbar(
                transferErr?.message || "Booking created, but lead transfer was not completed.",
                { variant: "warning" }
              );
            }
          } catch (transferError) {
            console.error("Failed to transfer projection lead:", transferError);
            enqueueSnackbar("Booking created, but lead transfer failed.", {
              variant: "warning",
            });
          }
        }

        const data = {
          email: res.booking.email,
          name: res.booking.company_name,
        };

        if (res.booking.term_1 != null) {
          try {
            Mailer(data);
            enqueueSnackbar("Welcome email sent!", { variant: "info" });
          } catch (mailErr) {
            console.warn("Welcome email failed (non-critical):", mailErr);
          }
        }

        enqueueSnackbar("Booking created successfully!", {
          variant: "success",
        });

        setFormData({
          branch: "",
          companyName: "",
          contactPerson: "",
          contactNumber: "",
          email: "",
          date: new Date().toISOString().split("T")[0],
          services: [],
          totalAmount: "",
          closed: "",
          selectTerm: "",
          amount: "",
          paymentDate: "",
          pan: "",
          gst: "",
          notes: "",
          bank: "",
          state: "",
          funddisbursement: "",
          isRefundable: false,
          refundablePercentage: "",
          isApprovalRefundable: false,
          approvalRefundablePercentage: "",
        });
        setSharedPersons([]);
        setShareCount(0);
        setPaymentProofs([]);
        setLoading(false);

        if (onClose) onClose();
      } catch (error) {
        console.error("Booking submission error:", error);
        enqueueSnackbar(`Error creating booking: ${error.message}`, {
          variant: "error",
        });
        setLoading(false);
      }
    }
  };


  const handleDialogClose = () => {
    setOpenDialog(false);
    if (onClose) onClose();
  };

  // Document upload handlers
  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    const newDocs = files.map(file => ({
      file,
      type: documentType,
      name: file.name,
      size: file.size
    }));
    setDocuments(prev => [...prev, ...newDocs]);
  };

  const handleRemoveDocument = (index) => {
    setDocuments(prev => prev.filter((_, i) => i !== index));
  };

  const uploadDocuments = async (bookingId) => {
    if (documents.length === 0) return;
    
    const userSession = JSON.parse(localStorage.getItem("userSession"));
    let uploaded = 0;
    let failed = 0;

    for (const doc of documents) {
      try {
        const formData = new FormData();
        formData.append("file", doc.file);
        formData.append("bookingId", bookingId);
        formData.append("documentType", doc.type);
        formData.append("notes", `Uploaded during booking creation`);

        const response = await fetch(`${apiUrl}/booking-documents/upload`, {
          method: "POST",
          headers: {
            authorization: `${userSession.token}`,
          },
          body: formData,
        });

        if (response.ok) {
          uploaded++;
        } else {
          failed++;
        }
      } catch (err) {
        console.error("Document upload error:", err);
        failed++;
      }
    }

    if (uploaded > 0) {
      enqueueSnackbar(`${uploaded} document(s) uploaded successfully`, { variant: "success" });
    }
    if (failed > 0) {
      enqueueSnackbar(`${failed} document(s) failed to upload`, { variant: "warning" });
    }
  };

  const bookingSearchLabel = (booking) => {
    const bookingDate = formatDateInput(booking?.date || booking?.createdAt);
    const bookingIdShort = booking?._id ? String(booking._id).slice(-6).toUpperCase() : "N/A";
    return `${booking?.company_name || "No Company"} | ${booking?.bdm || "Unknown"} | ${bookingDate || "No Date"} | ID: ${bookingIdShort}`;
  };

  const sectionCardSx = (tint, accent = "#94a3b8", inputTint = "#f8fbff") => ({
    p: { xs: 1.5, sm: 2.25 },
    borderRadius: "8px",
    border: "1px solid",
    borderColor: isDark ? "rgba(255,255,255,0.1)" : "rgba(148,163,184,0.18)",
    background: isDark
      ? `linear-gradient(180deg, rgba(15,23,42,0.92) 0%, rgba(15,23,42,0.8) 100%)`
      : `linear-gradient(180deg, ${tint} 0%, #ffffff 100%)`,
    boxShadow: isDark ? "none" : "0 14px 30px rgba(15,23,42,0.06)",
    "& .MuiOutlinedInput-root": {
      borderRadius: "8px",
      backgroundColor: isDark ? "rgba(15,23,42,0.72)" : inputTint,
      "& fieldset": {
        borderColor: isDark ? "rgba(255,255,255,0.14)" : "rgba(148,163,184,0.22)",
      },
      "&:hover fieldset": {
        borderColor: accent,
      },
      "&.Mui-focused fieldset": {
        borderColor: accent,
        boxShadow: isDark ? "none" : `0 0 0 3px ${accent}18`,
      },
    },
    "& .MuiInputBase-input::placeholder, & textarea::placeholder": {
      opacity: 1,
      color: isDark ? "rgba(248,250,252,0.48)" : "rgba(100,116,139,0.88)",
    },
    "& .MuiInputLabel-root.Mui-focused": {
      color: accent,
    },
    "& .MuiFormHelperText-root": {
      ml: 0.25,
    },
  });

  const SectionTitle = ({ icon, title, color }) => (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
      <Box
        sx={{
          width: 30,
          height: 30,
          borderRadius: "8px",
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          bgcolor: isDark ? `${color}22` : `${color}18`,
          color,
          flexShrink: 0,
        }}
      >
        {icon}
      </Box>
      <Typography variant="h6" sx={{ fontSize: { xs: "1rem", sm: "1.08rem" }, fontWeight: 700 }}>
        {title}
      </Typography>
    </Box>
  );

  return (
    <Box
      sx={{
        py: { xs: 1.5, sm: 2.5 },
        px: { xs: 1, sm: 2.5, md: 4 },
        width: "100%",
        "& .MuiOutlinedInput-root": {
          borderRadius: "8px",
        },
        "& .MuiTextField-root .MuiOutlinedInput-root, & .MuiFormControl-root .MuiOutlinedInput-root": {
          backgroundColor: isDark ? "rgba(15,23,42,0.75)" : "#f8fbff",
        },
        "& .MuiTextField-root .MuiFormHelperText-root, & .MuiFormControl-root .MuiFormHelperText-root": {
          ml: 0.25,
        },
      }}
    >
      <Box sx={{ maxWidth: 1180, mx: "auto" }}>
      <Typography
        variant="h5"
        component="h2"
        sx={{
          mb: 0.5,
          textAlign: { xs: "left", sm: "left" },
          fontWeight: 800,
          color: "text.primary",
        }}
      >
        Create New Booking
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Capture client details, booking information, revenue sharing, and attachments in one clean flow.
      </Typography>
      <form onSubmit={handleSubmit}>
        <Grid container spacing={2.25}>
          <Grid item xs={12}>
            <Paper elevation={0} sx={sectionCardSx("#f4f8ff", "#3b82f6", "#f7fbff")}>
              <SectionTitle icon={<BusinessOutlinedIcon fontSize="small" />} title="Client Information" color="#3b82f6" />
              <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth error={Boolean(errors.branch)}>
              <InputLabel>Branch *</InputLabel>
              <Select
                name="branch"
                value={formData.branch}
                onChange={handleChange}
                label="Branch *"
                variant="outlined"
              >
                <MenuItem value="">Select branch</MenuItem>
                {companyBranches.map((branch, idx) => (
                  <MenuItem key={idx} value={branch}>{branch}</MenuItem>
                ))}
              </Select>
              {errors.branch && <Typography color="error" variant="caption">{errors.branch}</Typography>}
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Company Name"
              name="companyName"
              value={formData.companyName}
              onChange={handleChange}
              placeholder="Enter company name"
              variant="outlined"
              error={Boolean(errors.companyName)}
              helperText={errors.companyName}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Contact Person Name *"
              name="contactPerson"
              value={formData.contactPerson}
              onChange={handleChange}
              placeholder="Enter contact person name"
              variant="outlined"
              error={Boolean(errors.contactPerson)}
              helperText={errors.contactPerson}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Contact Number *"
              name="contactNumber"
              type="text"
              value={formData.contactNumber}
              onChange={handleChange}
              placeholder="Enter contact number"
              variant="outlined"
              error={Boolean(errors.contactNumber)}
              helperText={errors.contactNumber}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Email ID *"
              name="email"
              type="email"
              value={formData.email}
              onChange={handleChange}
              placeholder="Enter email ID"
              variant="outlined"
              error={Boolean(errors.email)}
              helperText={errors.email}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Booking Date"
              name="date"
              type="date"
              value={formData.date}
              InputLabelProps={{ shrink: true }}
              disabled
              variant="outlined"
              helperText="Booking date is auto-set to today."
              sx={{
                '& input::-webkit-calendar-picker-indicator': {
                  filter: isDark ? 'invert(1)' : 'none',
                }
              }}
            />
          </Grid>
              </Grid>
            </Paper>
          </Grid>

          <Grid item xs={12}>
            <Paper elevation={0} sx={sectionCardSx("#faf7ff", "#8b5cf6", "#fbf7ff")}>
              <SectionTitle icon={<FactCheckOutlinedIcon fontSize="small" />} title="Booking Specifics" color="#8b5cf6" />
              <Grid container spacing={2}>
                <ServiceDropdown formData={formData} setFormData={setFormData} onCatalogLoaded={setDeductionCatalog} accentColor="#8b5cf6" surfaceColor="#fbf7ff" />
                <Grid item xs={12}>
                  <DeductionPreview catalog={deductionCatalog} source={previewSource} targetTerm={previewTermKey} booking={previewBooking} />
                </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Total Amount (GST included if applicable) *"
              name="totalAmount"
              type="text"
              value={formData.totalAmount}
              onChange={handleChange}
              placeholder="Enter total amount"
              disabled={isContinuationTerm}
              variant="outlined"
              error={Boolean(errors.totalAmount)}
              helperText={
                errors.totalAmount ||
                (isContinuationTerm
                  ? "Locked from the original booking for continuation terms."
                  : (!isCashPayment(formData.bank)
                    ? "Enter the final amount already including 18% GST."
                    : "Cash bookings do not include GST."))
              }
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <FormControl fullWidth error={Boolean(errors.selectTerm)}>
              <InputLabel>Select Term *</InputLabel>
              <Select
                name="selectTerm"
                value={formData.selectTerm}
                onChange={(e) => {
                  handleChange(e);
                  if (e.target.value === "Term 1") {
                    setSelectedSourceBooking(null);
                  }
                }}
                label="Select Term *"
                variant="outlined"
              >
                <MenuItem value="">Select Term</MenuItem>
                {TERM_LABELS.map((termLabel) => (
                  <MenuItem key={termLabel} value={termLabel}>{termLabel}</MenuItem>
                ))}
              </Select>
              {errors.selectTerm && <Typography color="error" variant="caption">{errors.selectTerm}</Typography>}
            </FormControl>
          </Grid>

          {isContinuationTerm && (
            <Grid item xs={12}>
              <Autocomplete
                options={termSourceBookings}
                value={selectedSourceBooking}
                onChange={handleSelectTermSourceBooking}
                loading={termSearchLoading}
                getOptionLabel={bookingSearchLabel}
                isOptionEqualToValue={(option, value) => option?._id === value?._id}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={`Search Term ${Math.max(1, selectedTermNumber - 1)} Booking`}
                    placeholder="Search by company, user, date, booking id"
                    error={Boolean(errors.termSource)}
                    helperText={errors.termSource || `Select existing Term ${Math.max(1, selectedTermNumber - 1)} record to continue receivable`}
                  />
                )}
              />
            </Grid>
          )}
              </Grid>
            </Paper>
          </Grid>

          <Grid item xs={12}>
            <Paper elevation={0} sx={sectionCardSx("#f4fbf6", "#10b981", "#f6fcf9")}>
              <SectionTitle icon={<AccountBalanceWalletOutlinedIcon fontSize="small" />} title="Financial Details" color="#10b981" />
              <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Received Amount (GST included if applicable) *"
              name="amount"
              type="text"
              value={formData.amount}
              onChange={handleChange}
              placeholder="Enter received amount"
              variant="outlined"
              error={Boolean(errors.amount)}
              helperText={errors.amount || "Revenue will be calculated after GST and deductions are removed."}
            />
          </Grid>

          {!isContinuationTerm && (
            <Grid item xs={12}>
              <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={Boolean(formData.isRefundable)}
                          onChange={(e) =>
                            setFormData((prev) => ({
                              ...prev,
                              isRefundable: e.target.checked,
                              isApprovalRefundable: e.target.checked ? false : prev.isApprovalRefundable,
                              approvalRefundablePercentage: e.target.checked ? "" : prev.approvalRefundablePercentage,
                            }))
                          }
                        />
                      }
                      label="Disbursement Guarantee Refundable Booking"
                    />
                    <TextField
                      fullWidth
                      sx={{ mt: 1 }}
                      size="small"
                      label="Deduction %"
                      name="refundablePercentage"
                      type="text"
                      value={formData.refundablePercentage}
                      onChange={handleChange}
                      onBlur={(e) => setNormalizedFormPercent("refundablePercentage", e.target.value, true)}
                      disabled={!formData.isRefundable}
                      error={Boolean(errors.refundablePercentage)}
                      helperText={errors.refundablePercentage || "This percentage is auto-deducted from revenue after GST removal."}
                      InputProps={{ endAdornment: <Typography sx={{ ml: 1 }}>%</Typography> }}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={Boolean(formData.isApprovalRefundable)}
                          onChange={(e) =>
                            setFormData((prev) => ({
                              ...prev,
                              isApprovalRefundable: e.target.checked,
                              isRefundable: e.target.checked ? false : prev.isRefundable,
                              refundablePercentage: e.target.checked ? "" : prev.refundablePercentage,
                            }))
                          }
                        />
                      }
                      label="Approval Guarantee Refundable Booking"
                    />
                    <TextField
                      fullWidth
                      sx={{ mt: 1 }}
                      size="small"
                      label="Guarantee %"
                      name="approvalRefundablePercentage"
                      type="text"
                      value={formData.approvalRefundablePercentage}
                      onChange={handleChange}
                      onBlur={(e) => setNormalizedFormPercent("approvalRefundablePercentage", e.target.value, true)}
                      disabled={!formData.isApprovalRefundable}
                      error={Boolean(errors.approvalRefundablePercentage)}
                      helperText={errors.approvalRefundablePercentage || "No automatic deduction is applied. It stays a normal revenue booking."}
                      InputProps={{ endAdornment: <Typography sx={{ ml: 1 }}>%</Typography> }}
                    />
                  </Grid>
                </Grid>
                {errors.refundableType && (
                  <Typography sx={{ mt: 1, color: "error.main", fontSize: "0.8rem", fontWeight: 600 }}>
                    {errors.refundableType}
                  </Typography>
                )}
              </Paper>
            </Grid>
          )}
              </Grid>
            </Paper>
          </Grid>

          <Grid item xs={12}>
            <Paper elevation={0} sx={sectionCardSx("#fff9ef", "#f59e0b", "#fffaf3")}>
              <SectionTitle icon={<GroupAddOutlinedIcon fontSize="small" />} title="Revenue Sharing" color="#f59e0b" />
              <Grid container spacing={2}>
          <Grid item xs={12}>
            <FormControl fullWidth>
              <InputLabel>Number of Salespersons to Share</InputLabel>
              <Select
                value={shareCount}
                label="Number of Salespersons to Share"
                onChange={(e) => {
                  const count = parseInt(e.target.value);
                  setShareCount(count);
                  setSharedPersons(Array.from({ length: count }, () => ({ userId: "", percentage: "" })));
                }}
              >
                <MenuItem value={1}>1</MenuItem>
                <MenuItem value={2}>2</MenuItem>
                <MenuItem value={3}>3</MenuItem>
                <MenuItem value={4}>4</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          {sharedPersons.map((person, index) => (
            <Grid container spacing={2} key={index} sx={{ mt: 1, px: 2 }}>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth error={!person.userId}>
                  <InputLabel>Share with User</InputLabel>
                  <Select
                    value={person.userId}
                    label="Share with User"
                    onChange={(e) => {
                      const updated = [...sharedPersons];
                      updated[index].userId = e.target.value;
                      setSharedPersons(updated);
                    }}
                  >
                    {users.map((u) => (
                      <MenuItem key={u._id} value={u._id}>
                        {u.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Percentage Share"
                  type="text"
                  value={person.percentage}
                  onChange={(e) => setNormalizedSharedPercent(index, e.target.value)}
                  onBlur={(e) => setNormalizedSharedPercent(index, e.target.value, true)}
                  placeholder="Blank = equal split"
                  InputProps={{
                    endAdornment: <Typography sx={{ mr: 1, color: 'text.secondary' }}>%</Typography>
                  }}
                />
              </Grid>
            </Grid>
          ))}
              </Grid>
            </Paper>
          </Grid>

          <Grid item xs={12}>
            <Paper elevation={0} sx={sectionCardSx("#fff8f2", "#f97316", "#fff8f2")}>
              <SectionTitle icon={<NotesOutlinedIcon fontSize="small" />} title="Follow-up and Notes" color="#f97316" />
              <Grid container spacing={2}>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Closed By"
              name="closed"
              value={formData.closed}
              onChange={handleChange}
              placeholder="Lead closed by"
              variant="outlined"
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Payment Date *"
              name="paymentDate"
              type="date"
              value={formData.paymentDate}
              onChange={handleChange}
               InputLabelProps={{ shrink: true }}
              variant="outlined"
              error={Boolean(errors.paymentDate)}
              helperText={errors.paymentDate}
              sx={{
                '& input::-webkit-calendar-picker-indicator': {
                  filter: isDark ? 'invert(1)' : 'none',
                }
              }}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="PAN Number *"
              name="pan"
              value={formData.pan}
              onChange={handleChange}
              placeholder="e.g. ABCDE1234F"
              variant="outlined"
              error={Boolean(errors.pan)}
              helperText={errors.pan}
              inputProps={{ style: { textTransform: 'uppercase' } }}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="GST Number"
              name="gst"
              value={formData.gst}
              onChange={handleChange}
              placeholder="Enter GST"
              variant="outlined"
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              <InputLabel>Payment Mode</InputLabel>
              <Select
                name="bank"
                value={formData.bank}
                onChange={handleChange}
                variant="outlined"
              >
                <MenuItem value="">Select Payment Mode</MenuItem>
                {BOOKING_PAYMENT_METHODS.map((paymentMethod) => (
                  <MenuItem key={paymentMethod} value={paymentMethod}>{paymentMethod}</MenuItem>
                ))}
              </Select>
              {errors.bank && <Typography color="error" variant="caption">{errors.bank}</Typography>}
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="After Fund Disbursement"
              name="funddisbursement"
              value={formData.funddisbursement}
              onChange={handleChange}
              onBlur={(e) => setNormalizedFormPercent("funddisbursement", e.target.value, true)}
              placeholder="Enter percentage"
              variant="outlined"
              error={Boolean(errors.funddisbursement)}
              helperText={errors.funddisbursement}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <FormControl fullWidth error={Boolean(errors.state)}>
              <InputLabel>Select State *</InputLabel>
              <Select
                name="state"
                value={formData.state}
                onChange={handleChange}
                label="Select State *"
                variant="outlined"
              >
                <MenuItem value="">Select State</MenuItem>
                <MenuItem value="Andhra Pradesh">Andhra Pradesh</MenuItem>
                <MenuItem value="Arunachal Pradesh">Arunachal Pradesh</MenuItem>
                <MenuItem value="Assam">Assam</MenuItem>
                <MenuItem value="Bihar">Bihar</MenuItem>
                <MenuItem value="Chhattisgarh">Chhattisgarh</MenuItem>
                <MenuItem value="Goa">Goa</MenuItem>
                <MenuItem value="Gujarat">Gujarat</MenuItem>
                <MenuItem value="Haryana">Haryana</MenuItem>
                <MenuItem value="Himachal Pradesh">Himachal Pradesh</MenuItem>
                <MenuItem value="Jharkhand">Jharkhand</MenuItem>
                <MenuItem value="Karnataka">Karnataka</MenuItem>
                <MenuItem value="Kerala">Kerala</MenuItem>
                <MenuItem value="Madhya Pradesh">Madhya Pradesh</MenuItem>
                <MenuItem value="Maharashtra">Maharashtra</MenuItem>
                <MenuItem value="Manipur">Manipur</MenuItem>
                <MenuItem value="Meghalaya">Meghalaya</MenuItem>
                <MenuItem value="Mizoram">Mizoram</MenuItem>
                <MenuItem value="Nagaland">Nagaland</MenuItem>
                <MenuItem value="Odisha">Odisha</MenuItem>
                <MenuItem value="Punjab">Punjab</MenuItem>
                <MenuItem value="Chandigarh">Chandigarh</MenuItem>
                <MenuItem value="Rajasthan">Rajasthan</MenuItem>
                <MenuItem value="Sikkim">Sikkim</MenuItem>
                <MenuItem value="Tamil Nadu">Tamil Nadu</MenuItem>
                <MenuItem value="Telangana">Telangana</MenuItem>
                <MenuItem value="Tripura">Tripura</MenuItem>
                <MenuItem value="Uttar Pradesh">Uttar Pradesh</MenuItem>
                <MenuItem value="Uttarakhand">Uttarakhand</MenuItem>
                <MenuItem value="West Bengal">West Bengal</MenuItem>
                <MenuItem value="Delhi">Delhi</MenuItem>
                <MenuItem value="Andaman and Nicobar">Andaman and Nicobar</MenuItem>
                <MenuItem value="Jammu and Kashmir">Jammu and Kashmir</MenuItem>
              </Select>
              {errors.state && <Typography color="error" variant="caption">{errors.state}</Typography>}
            </FormControl>
          </Grid>


          {/* Notes Field (Full Width) */}
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Notes"
              name="notes"
              value={formData.notes}
              onChange={handleChange}
              placeholder="Enter any notes"
              multiline
              rows={3}
              variant="outlined"
            />
          </Grid>
              </Grid>
            </Paper>
          </Grid>

          <Grid item xs={12}>
            <Paper elevation={0} sx={sectionCardSx("#fff7fb", "#ec4899", "#fff9fc")}>
              <SectionTitle icon={<AttachFileOutlinedIcon fontSize="small" />} title="Attachments" color="#ec4899" />
              <Grid container spacing={2}>
          <Grid item xs={12}>
            <Button
              component="label"
              variant={errors.paymentProof ? "contained" : "outlined"}
              color={errors.paymentProof ? "error" : "primary"}
              startIcon={<CloudUploadIcon />}
              fullWidth
              sx={{ minHeight: 48, borderRadius: "8px" }}
            >
              {paymentProofs.length > 0 ? `${paymentProofs.length} payment proof file(s) selected` : "Attach Payment Screenshot(s) *"}
              <input
                type="file"
                hidden
                accept="image/*,.pdf"
                multiple
                onChange={(e) => {
                  const nextFiles = Array.from(e.target.files || []);
                  setPaymentProofs((prev) => [...prev, ...nextFiles]);
                  e.target.value = "";
                }}
              />
            </Button>
            {paymentProofs.length > 0 && (
              <Box sx={{ mt: 1, display: "grid", gap: 0.5 }}>
                {paymentProofs.map((file, index) => (
                  <Chip
                    key={`${file.name}-${index}`}
                    label={file.name}
                    onDelete={() => setPaymentProofs((prev) => prev.filter((_, i) => i !== index))}
                    size="small"
                    variant="outlined"
                  />
                ))}
              </Box>
            )}
            {errors.paymentProof && (
              <Typography color="error" variant="caption" sx={{ mt: 0.5, display: "block" }}>
                {errors.paymentProof}
              </Typography>
            )}
          </Grid>

          {/* Document Upload Section */}
          <Grid item xs={12}>
            <Paper
              elevation={0}
              sx={{
                p: 2,
                border: "1px dashed",
                borderColor: "divider",
                borderRadius: 2,
                bgcolor: "background.paper",
              }}
            >
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                Upload Documents (Optional)
              </Typography>
              
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Document Type</InputLabel>
                    <Select
                      value={documentType}
                      onChange={(e) => setDocumentType(e.target.value)}
                      label="Document Type"
                    >
                      <MenuItem value="agreement">Agreement</MenuItem>
                      <MenuItem value="pitch_deck">Pitch Deck</MenuItem>
                      <MenuItem value="dpr">DPR</MenuItem>
                      <MenuItem value="application">Application</MenuItem>
                      <MenuItem value="aadhaar">Aadhaar</MenuItem>
                      <MenuItem value="pan">PAN</MenuItem>
                      <MenuItem value="others">Others</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Button
                    component="label"
                    variant="outlined"
                    startIcon={<CloudUploadIcon />}
                    fullWidth
                    size="small"
                  >
                    Select Files
                    <input
                      type="file"
                      hidden
                      multiple
                      onChange={handleFileSelect}
                    />
                  </Button>
                </Grid>
              </Grid>

              {/* Selected Files List */}
              {documents.length > 0 && (
                <Box sx={{ mt: 2 }}>
                  {documents.map((doc, index) => (
                    <Chip
                      key={index}
                      icon={<DescriptionIcon />}
                      label={`${doc.name} (${doc.type})`}
                      onDelete={() => handleRemoveDocument(index)}
                      deleteIcon={<DeleteIcon />}
                      sx={{ m: 0.5 }}
                      size="small"
                      color="primary"
                      variant="outlined"
                    />
                  ))}
                </Box>
              )}
            </Paper>
          </Grid>
              </Grid>
            </Paper>
          </Grid>

          {/* Loader */}
          {loading && (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                width: "100%",
                marginTop: "20px",
              }}
            >
              <CircularProgress size={24} />
            </Box>
          )}

          <Grid item xs={12}>
            <Button
              variant="contained"
              color="primary"
              type="submit"
              fullWidth
              disabled={loading} // Disable the submit button when loading
              sx={{
                mt: 0.5,
                minHeight: 52,
                borderRadius: "8px",
                fontWeight: 700,
                boxShadow: isDark ? "none" : "0 12px 22px rgba(255,59,31,0.18)",
              }}
              startIcon={<SendRoundedIcon />}
            >
              {loading ? "Submitting..." : "Submit Booking"}
            </Button>
          </Grid>
        </Grid>
      </form>
      </Box>
    </Box>
  );
};

export default AddBooking;
